import { Identifier } from 'react-admin';

/**
 * Upper bound of rows one Select all may pick. Matches the batch size the
 * *-paid endpoints accept, so a capped selection always fits one Mark as Paid
 * run.
 */
export const SELECT_ALL_MAX_ROWS = 1000;

/**
 * A selected id whose row is not in `rowsById` (e.g. selected on another page)
 * counts as not approvable, so Approve stays disabled until the row can be
 * checked.
 */
export const areAllSelectedRowsApprovable = <RowType>(
  selectedIds: Identifier[],
  rowsById: { [id: string]: RowType | undefined },
  isApprovable: (row: RowType) => boolean,
): boolean =>
  selectedIds.every(id => {
    const row = rowsById[id];
    return row !== undefined && isApprovable(row);
  });

/** Filter keys holding the two bounds of a date range. */
export type DateRangeFields = {
  after: string;
  before: string;
};

/** Filter keys of a react-admin date range on `field` (`||$gte` / `||$lte`). */
export const dateRangeFields = (field: string): DateRangeFields => ({
  after: `${field}||$gte`,
  before: `${field}||$lte`,
});

const isSet = (value: unknown): boolean =>
  value !== undefined && value !== null && value !== '';

export const isDateRangeSet = (
  filterValues: { [key: string]: unknown } | undefined,
  { after, before }: DateRangeFields,
): boolean => isSet(filterValues?.[after]) && isSet(filterValues?.[before]);

/** "N row(s) marked as paid." plus " M skipped." when any row was skipped. */
export const markedAsPaidMessage = (
  markedCount: number,
  skippedCount: number,
): string => {
  const rows = markedCount === 1 ? 'row' : 'rows';
  const skipped = skippedCount > 0 ? ` ${skippedCount} skipped.` : '';
  return `${markedCount} ${rows} marked as paid.${skipped}`;
};

/** Question asked before a Mark as Paid run; `count` is the selection size. */
export const markAsPaidConfirmMessage = (count: number): string => {
  const rows = count === 1 ? 'row' : 'rows';
  return `Are you sure you want to Mark ${count} ${rows} as Paid? Rows already marked as Paid will not change.`;
};

export const selectAllCapMessage = (
  cap: number = SELECT_ALL_MAX_ROWS,
): string =>
  `Select all is limited to ${cap} rows. Refine the filters and try again.`;
